import { runtimeEnv } from './runtimeEnv';
/**
 * Which sibling hosts belong to the origin the app is currently served from.
 *
 * The editor and the Unity WebGL iframe must share a parent domain (cross-site
 * framing makes the previewer ~10x slower), and the product is served from two
 * origins during the lureo.app -> create.lureo.com move (lureo-docs#348). One
 * build therefore cannot bake a single Unity host: it is resolved from
 * `window.location.hostname`, with the `VITE_*` env vars as the fallback for
 * localhost and any host not listed here. The exception is `VITE_UNITY_URL`,
 * which wins when it is same-site with the page: see `getUnityBaseUrl`.
 *
 * Outbound share hosts (native deep links on `/open`, the WebXR / App Clip
 * host) are on the `.com` hosts for every production origin: 1.14, the first
 * release registering them, is live and the minimum version (decision D6).
 * The `.app` hosts keep answering for links already printed.
 *
 * There is deliberately no production fallback for an origin that is not
 * listed and has no env var (share links 3.7): a staging or preview host
 * must fail visibly rather than hand out production links.
 */
export interface ProductHosts {
  /** Unity WebGL origin the editor iframes when the editor URL setting is unset. Always same-site with the page. */
  unity: string;
  /** Host that native-app deep links (`/open?...`) are minted on. */
  deepLink: string;
  /** WebXR viewer / App Clip invocation host. */
  webxr: string;
  /** Origin share links (`/s/<slug>`) are minted on — the one-way door of decision S1. */
  share: string;
}

const HOSTS_BY_ORIGIN: Record<string, ProductHosts> = {
  'lureo.app': {
    unity: 'https://unity.lureo.app',
    deepLink: 'https://create.lureo.com',
    webxr: 'https://xr.lureo.com',
    share: 'https://create.lureo.com',
  },
  'create.lureo.com': {
    unity: 'https://unity.lureo.com',
    deepLink: 'https://create.lureo.com',
    webxr: 'https://xr.lureo.com',
    share: 'https://create.lureo.com',
  },
  // Unlike production, staging does not wait for app builds claiming these hosts: its
  // links open in the browser until they ship.
  'create.staging.lureo.com': {
    unity: 'https://unity.staging.lureo.com',
    deepLink: 'https://create.staging.lureo.com',
    webxr: 'https://xr.staging.lureo.com',
    share: 'https://create.staging.lureo.com',
  },
};

function env(name: string): string | undefined {
  const value = (runtimeEnv[name] ?? undefined) as string | undefined;
  return value && value.trim() ? value.trim() : undefined;
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

function currentHosts(): ProductHosts | undefined {
  if (typeof window === 'undefined') return undefined;
  return HOSTS_BY_ORIGIN[window.location.hostname.toLowerCase()];
}

// Registrable domain by its last two labels: right for every host the product is served from.
function siteOf(url: URL): string {
  return `${url.protocol}//${url.hostname.toLowerCase().split('.').slice(-2).join('.')}`;
}

function isSameSiteWithPage(base: string): boolean {
  try {
    return siteOf(new URL(base)) === siteOf(new URL(window.location.origin));
  } catch {
    return false;
  }
}

const warnedCrossSite = new Set<string>();

/**
 * Base URL of the editor's 3D view to iframe: the Unity WebGL build (`https://unity.lureo.com`)
 * or the three.js editor (`https://editor.lureo.com`).
 *
 * The editor URL setting `VITE_UNITY_URL` (a Fly secret, see docker/runtime-config.sh) selects
 * that host and is the switch between Unity and three.js (decision D47). On a listed origin it
 * wins over the origin's Unity host, which stays the fallback when the setting is unset or is
 * not same-site with the page.
 */
export function getUnityBaseUrl(): string | undefined {
  const setting = env('VITE_UNITY_URL');
  const hosts = currentHosts();
  if (!hosts) return setting;
  if (!setting) return hosts.unity;
  if (isSameSiteWithPage(setting)) return stripTrailingSlash(setting);
  const key = `${window.location.hostname} ${setting}`;
  if (!warnedCrossSite.has(key)) {
    warnedCrossSite.add(key);
    console.warn(`[productHosts] VITE_UNITY_URL ${setting} is not same-site with ${window.location.origin}; using ${hosts.unity}`);
  }
  return hosts.unity;
}

/** Origin (scheme + host) of the Unity iframe, or undefined when it is same-origin/relative. */
export function getUnityOrigin(): string | undefined {
  const base = getUnityBaseUrl();
  if (!base) return undefined;
  try {
    return new URL(base).origin;
  } catch {
    return undefined;
  }
}

/** Host native-app deep links are minted on, without a trailing slash, or undefined when unconfigured. */
export function getDeepLinkBaseUrl(): string | undefined {
  const base = currentHosts()?.deepLink ?? env('VITE_APP_URL');
  return base ? stripTrailingSlash(base) : undefined;
}

/** WebXR viewer / App Clip host without a trailing slash, or undefined when unconfigured. */
export function getWebxrBaseUrl(): string | undefined {
  const base = currentHosts()?.webxr ?? env('VITE_XR_URL') ?? env('VITE_WEBXR_URL');
  return base ? stripTrailingSlash(base) : undefined;
}

/**
 * Origin share links are minted on. Falls back to the page's own origin, which is right for
 * localhost and for any host serving this SPA: the `/s/:slug` landing lives in this app.
 */
export function getShareLinkBaseUrl(): string {
  const base = currentHosts()?.share ?? env('VITE_SHARE_URL') ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return stripTrailingSlash(base);
}
